import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getMapCases } from "@/lib/api";

const CATEGORY_COLORS = {
  Theft: "#C8A24A",
  Forgery: "#7FB3D5",
  Looting: "#C0392B",
  Restitution: "#52BE80",
  Censorship: "#A569BD",
  Fraud: "#E67E22",
  Trafficking: "#EC7063",
};
const colorFor = (cat) => CATEGORY_COLORS[cat] || "#8A8578";

export function AtlasTeaser() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    getMapCases().then(setPoints).catch(() => setPoints([]));
  }, []);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [40, 6],
      zoom: 2,
      minZoom: 2,
      maxZoom: 2,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
    });
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 16,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !points.length) return;
    const layer = L.layerGroup().addTo(map);
    points.forEach((p) => {
      L.circleMarker([p.lat, p.lng], {
        radius: 4,
        color: colorFor(p.category),
        weight: 1,
        fillColor: colorFor(p.category),
        fillOpacity: 0.7,
        interactive: false,
      }).addTo(layer);
    });
    return () => layer.remove();
  }, [points]);

  return (
    <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 border-t border-line" data-testid="atlas-teaser">
      <div className="grid items-stretch gap-8 lg:grid-cols-12">
        {/* Copy */}
        <div className="lg:col-span-5 flex flex-col justify-center">
          <div className="eyebrow mb-4 text-gold flex items-center gap-2">
            <MapPin size={14} /> The Atlas
          </div>
          <h3 className="font-serif text-3xl leading-tight text-parchment lg:text-4xl">
            Every case, mapped where it happened.
          </h3>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-parchment/70">
            From the Louvre to Boston, Baghdad to São Paulo — trace the geography of art
            crime across the archive. Filter by theft, forgery, looting, and more, then open
            any point to read the dossier.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2">
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
              <span key={cat} className="flex items-center gap-2 font-mono text-xs text-faint">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                {cat}
              </span>
            ))}
          </div>
          <Link
            to="/map"
            data-testid="atlas-teaser-cta"
            className="mt-8 inline-flex w-fit items-center gap-2 bg-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-ink hover:bg-gold-hover transition-colors"
          >
            Explore the full map <ArrowUpRight size={16} />
          </Link>
        </div>

        {/* Live mini-map (whole panel links to /map) */}
        <div className="lg:col-span-7">
          <Link to="/map" aria-label="Explore the full map" className="group relative block border border-line">
            <div
              ref={mapEl}
              data-testid="atlas-teaser-map"
              className="h-[340px] w-full lg:h-[420px] pointer-events-none"
              style={{ background: "#0b0b0d" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 border border-gold/50 bg-ink/70 px-4 py-2 eyebrow text-gold backdrop-blur-sm transition-colors group-hover:bg-gold group-hover:text-ink">
              Open the Atlas <ArrowUpRight size={14} />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default AtlasTeaser;
